import { ISocket } from '@/state/AuthenticationStore/types';
import Logger from '../Logger';
import { SocketResponse } from '@/types/socketResponse.type';

enum INTERCEPTOR_TYPE {
  REQUEST = 'Request',
  RESPONSE = 'Response',
}

// Type-safe Interceptor
interface Interceptor<T = unknown, R = unknown> {
  type: INTERCEPTOR_TYPE;
  success: (
    request: SocketEmitRequest<T>,
    response: SocketResponse<R>,
  ) => Promise<SocketResponse<R>>;
  error: (
    request: SocketEmitRequest<T>,
    response: SocketResponse<R>,
  ) => Promise<SocketResponse<R>>;
}

interface InterceptorResponse<T, R> {
  request?: SocketEmitRequest<T>;
  response: SocketResponse<R>;
}

export interface SocketConfig<T> {
  data: T;
  timeout?: number;
  checkIsSocketConnected?: boolean;
}

interface SocketEmitRequest<T> {
  eventName: string;
  config: SocketConfig<T>;
}

export class SocketRequestHelper {
  socket: ISocket | null = null;
  interceptors: Interceptor<any, any>[] = [];
  factory = { create: () => new SocketRequestHelper().create(this.socket) };

  constructor() {}

  create(socket: ISocket | null) {
    this.socket = socket;
    return this;
  }

  use<T = unknown, R = unknown>(
    success: (
      request: SocketEmitRequest<T>,
      response: SocketResponse<R>,
    ) => Promise<SocketResponse<R>>,
    error: (
      request: SocketEmitRequest<T>,
      response: SocketResponse<R>,
    ) => Promise<SocketResponse<R>>,
    type: INTERCEPTOR_TYPE = INTERCEPTOR_TYPE.RESPONSE,
  ) {
    const interceptor: Interceptor<T, R> = { success, error, type };
    this.interceptors.push(interceptor);
    return () => {
      this.interceptors = this.interceptors.filter(i => i !== interceptor);
    };
  }

  emit<T = unknown, R = unknown>(
    eventName: string,
    config: SocketConfig<T>,
  ): Promise<SocketResponse<R>> {
    const { data, timeout = 30000, checkIsSocketConnected = false } = config;
    return new Promise<SocketResponse<R>>((resolve, reject) => {
      let isCalled = false;
      const request: SocketEmitRequest<T> = {
        eventName,
        config,
      };

      const timer = setTimeout(() => {
        if (!isCalled) {
          isCalled = true;
          Logger.error(
            `[SocketRequest] Request to ${eventName} timed out after ${timeout}ms. Possible causes: no server response, dropped packet, or oversized payload.`,
          );
          reject(new Error(`SocketRequest to ${eventName} timed out`));
        }
      }, timeout);

      if (
        (checkIsSocketConnected && this.socket?.connected) ||
        !checkIsSocketConnected
      ) {
        try {
          this.socket?.emit(
            eventName,
            { event_name: eventName, data },
            async (response: SocketResponse<R>) => {
              if (!isCalled) {
                isCalled = true;
                clearTimeout(timer);
                const { response: processedResponse } =
                  await this.processInterceptor<T, R>(request, response);
                resolve(processedResponse);
              }
            },
          );
        } catch (err) {
          clearTimeout(timer);
          Logger.error(
            `[SocketRequest] Emit error for ${eventName}: ${String(err)}`,
          );
          reject(err);
        }
      } else {
        clearTimeout(timer);
        Logger.error(`[SocketRequest] Socket not connected for ${eventName}`);
        reject(new Error(`Socket not connected for ${eventName}`));
      }
    });
  }

  private async processInterceptor<T = unknown, R = unknown>(
    request: SocketEmitRequest<T>,
    response: SocketResponse<R>,
  ): Promise<InterceptorResponse<T, R>> {
    for (const interceptor of this.interceptors) {
      try {
        if (response?.error) {
          const res = await interceptor.error(request, response);
          return Promise.resolve({ request, response: res });
        }
        const res = await interceptor.success(request, response);
        return Promise.resolve({ request, response: res });
      } catch (error) {
        const res = await interceptor.error(request, response);
        return Promise.resolve({ request, response: res });
      }
    }
    return Promise.resolve({ request, response });
  }
  ejectInterceptor() {
    this.interceptors = [];
  }
}
